/** Core domain types for Markcat. Mirror of backend API models. */

/** Slim list item served by GET /api/bookmarks. The title/summary are resolved
 *  server-side for the tenant's display language (display_title / display_summary
 *  / summary_is_fallback); the raw language variants live on BookmarkDetail, which
 *  the drawer fetches for its per-item language switcher. */
export interface BookmarkListItem {
  id: string;
  url: string;
  display_title?: string | null;
  display_summary?: string | null;
  summary_is_fallback?: boolean | null;
  /** 1 if any translated summary exists — the drawer's language switcher has options. */
  has_translations?: number | null;
  category_path?: string | null;
  status: BookmarkStatus;
  last_error?: string | null;
  extraction_method?: string | null;
  content_type?: string | null;
  pdf_pages?: number | null;
  date?: number | null;
  updated_at?: number | null;
  /** Each image: 1 when it exists, and its version. A card requests an image
   *  only when it exists, under a URL carrying the version (`bookmarkImagePath`)
   *  — the image caches never revalidate, and every write moves the version. */
  has_screenshot?: number | null;
  screenshot_version?: number | null;
  has_og_image?: number | null;
  og_image_version?: number | null;
  has_favicon?: number | null;
  favicon_version?: number | null;
  is_broken_link?: number | null;
  is_bot_protected?: number | null;
  is_dead?: number | null;
  is_favourite?: number | null;
  confidence?: number | null;
  domain?: string | null;
}

export interface BookmarkDetail extends BookmarkListItem {
  title?: string | null;
  summary_original?: string | null;
  summary_lang1?: string | null;
  summary_lang2?: string | null;
  summary_lang3?: string | null;
  title_lang1?: string | null;
  title_lang2?: string | null;
  title_lang3?: string | null;
  quality_reason?: string | null;
  final_url?: string | null;
  content_raw?: string | null;
  content_length?: number | null;
  language?: string | null;
  tags?: string | null;
  import_tag?: string | null;
  source?: string | null;
  favicon_source?: string | null;
  content_parser?: string | null;
}

export const BOOKMARK_STATUSES = [
  'new',
  'processing',
  'needs_browser',
  'needs_stealth',
  'needs_scraper',
  'needs_subcategory',
  'extracted',
  'pending_classification',
  'categorized',
  'invalid',
  'classification_failed',
] as const;
export type BookmarkStatus = typeof BOOKMARK_STATUSES[number];

// Status helpers — mirror service/database.py Status.is_terminal().
// Terminal = no more processing. Failed = terminal-but-bad. Everything else is in flight.
const TERMINAL_STATUSES = new Set<string>(['categorized', 'invalid', 'classification_failed']);
const FAILED_STATUSES = new Set<string>(['invalid', 'classification_failed']);

export function isFailedStatus(status: string): boolean {
  return FAILED_STATUSES.has(status);
}

/** In flight: any non-terminal status (incl. resumable needs_browser/needs_stealth/needs_scraper). */
export function isPendingStatus(status: string): boolean {
  return !TERMINAL_STATUSES.has(status);
}

export interface Category {
  path: string;
  name: string;
  count: number;
  children: Category[];
  depth: number;
  icon?: string | null;
  color?: string | null;
}

export interface CategoryMeta {
  path: string;
  icon?: string | null;
  color?: string | null;
}

/** User intent emitted by the category tree and executed by the API client. */
export interface CategoryAction {
  type: 'rename' | 'move-root' | 'delete' | 'move' | 'create';
  path: string;
  name: string;
  targetParent?: string | null;
  /** Delete only: true also deletes bookmarks; false re-homes them. */
  cascade?: boolean;
}

export interface User {
  id: string;
  email: string;
  plan: string;
  created_at?: number | null;
}

export interface WorkerStatus {
  control_allowed: boolean;
  is_paused_effective: boolean;
  workers_active: number;
  queue_size: number;
  backlog?: BacklogCounts | null;
  avg_classify_ms?: number | null;
  avg_prefetch_ms?: number | null;
  avg_favicon_ms?: number | null;
  avg_og_image_ms?: number | null;
  loops?: Record<string, string> | null;
}

export interface BacklogCounts {
  new: number;
  processing: number;
  extracted: number;
  pending_classification: number;
  needs_browser: number;
  needs_stealth: number;
  needs_scraper: number;
  invalid: number;
  categorized: number;
  classification_failed: number;
  dead_links: number;
  favicon_pending: number;
  og_image_pending: number;
  screenshot_pending: number;
}

export interface WorkerLog {
  id: number;
  timestamp: string;
  loop: string;
  level: string;
  message: string;
  bookmark_id?: string | null;
  duration_ms?: number | null;
  url?: string | null;
  has_llm_data: boolean;
  // Detail fields (only from detail endpoint)
  llm_provider?: string | null;
  llm_model?: string | null;
  llm_response?: string | null;
  llm_stage?: number | null;
  details?: Record<string, unknown> | null;
}

export interface BookmarkEvent {
  eventType: string;
  id: string;
  title?: string | null;
  url?: string | null;
  status?: string | null;
  category_path?: string | null;
  confidence?: number | null;
  // Slim display fields for the list-cache patch (classified/extracted events),
  // resolved server-side for the tenant's display language. `title` stays for the
  // toast copy; the cache merge uses these.
  display_title?: string | null;
  display_summary?: string | null;
  summary_is_fallback?: boolean | null;
  has_translations?: number | null;
  // Remaining slim-row fields a bookmark.created / .updated payload carries, so
  // the client can splice or replace a full BookmarkListItem. bookmark.deleted
  // carries only id and date (the default list's sort key).
  date?: number | null;
  updated_at?: number | null;
  domain?: string | null;
  is_favourite?: number | null;
  has_screenshot?: number | null;
  screenshot_version?: number | null;
  has_og_image?: number | null;
  og_image_version?: number | null;
  has_favicon?: number | null;
  favicon_version?: number | null;
  is_broken_link?: number | null;
  is_bot_protected?: number | null;
  is_dead?: number | null;
  extraction_method?: string | null;
  content_type?: string | null;
  pdf_pages?: number | null;
  last_error?: string | null;
}

export const VIEW_MODES = ['list', 'tiles', 'timeline'] as const;
export type ViewMode = typeof VIEW_MODES[number];

/** Tiles-per-row choice. 'auto' lets BookmarkTiles fit columns to width; a
 *  number freezes the grid to that many columns. Per-surface (not a backend
 *  setting), mirroring viewMode. */
export const TILE_COLUMNS = ['auto', 1, 2, 3, 4, 5] as const;
export type TileColumns = typeof TILE_COLUMNS[number];
export type SortField = 'date' | 'title' | 'category' | 'confidence';
export type SortDirection = 'asc' | 'desc';
export type Theme = 'system' | 'daylight' | 'dusk' | 'midnight';
export type OpenBehavior = 'current' | 'newtab' | 'newwindow';
